import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

const StudentBehaviorRadarChart = ({ student, onClose, onSave }) => {
  const [ratings, setRatings] = useState({
    disciplineRules: 3,
    classParticipation: 3,
    respectfulness: 3,
    homeworkCompletion: 3,
    selfControl: 3
  });
  const [loading, setLoading] = useState(true);

  const canvasRef = useRef(null);

  const categories = [
    { key: 'disciplineRules', label: 'Discipline / Rules Follow' },
    { key: 'classParticipation', label: 'Class Participation' },
    { key: 'respectfulness', label: 'Respect towards Teachers and Students' },
    { key: 'homeworkCompletion', label: 'Homework Completion' },
    { key: 'selfControl', label: 'Self-Control & Emotional Behaviour' }
  ];

  const getColor = (value) => {
    if (value <= 2) return '#ef4444';
    if (value === 3) return '#eab308';
    if (value === 4) return '#3b82f6';
    return '#22c55e';
  };

  // Load existing behavior evaluation data
  useEffect(() => {
    const loadExistingData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/behavior-evaluation/${student._id}`);
        if (response.data && response.data.behaviorRatings) {
          setRatings(response.data.behaviorRatings);
          console.log('Loaded existing behavior data:', response.data.behaviorRatings);
        } else {
          console.log('No existing behavior data found, using defaults');
        }
      } catch (error) {
        console.error('Error loading behavior data:', error);
        // Keep default ratings if error occurs
      } finally {
        setLoading(false);
      }
    };

    if (student && student._id) {
      loadExistingData();
    }
  }, [student]);

  const drawRadarChart = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = 120;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Calculate total and angles for pie chart
    const total = Object.values(ratings).reduce((a, b) => a + b, 0);
    let currentAngle = -Math.PI / 2;

    // Draw pie slices
    categories.forEach((category, index) => {
      const value = ratings[category.key];
      const sliceAngle = (value / total) * 2 * Math.PI;
      const color = getColor(value);
      
      // Draw slice
      ctx.beginPath();
      ctx.moveTo(centerX, centerY);
      ctx.arc(centerX, centerY, radius, currentAngle, currentAngle + sliceAngle);
      ctx.closePath();
      
      ctx.fillStyle = color;
      ctx.fill();
      
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 3;
      ctx.stroke();
      
      // Draw label with value
      const labelAngle = currentAngle + sliceAngle / 2;
      const labelRadius = radius + 35;
      const labelX = centerX + Math.cos(labelAngle) * labelRadius;
      const labelY = centerY + Math.sin(labelAngle) * labelRadius;
      
      ctx.fillStyle = '#1f2937';
      ctx.font = 'bold 12px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`${value}`, labelX, labelY - 5);
      
      ctx.font = '10px Arial';
      ctx.fillStyle = '#4b5563';
      const words = category.label.split(' ');
      if (words.length > 2) {
        ctx.fillText(words.slice(0, 2).join(' '), labelX, labelY + 8);
        ctx.fillText(words.slice(2).join(' '), labelX, labelY + 18);
      } else {
        ctx.fillText(category.label, labelX, labelY + 8);
      }
      
      currentAngle += sliceAngle;
    });

    // Center circle with overall score
    ctx.beginPath();
    ctx.arc(centerX, centerY, 35, 0, 2 * Math.PI);
    const centerGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, 35);
    centerGradient.addColorStop(0, '#ffffff');
    centerGradient.addColorStop(1, '#f3f4f6');
    ctx.fillStyle = centerGradient;
    ctx.fill();
    ctx.strokeStyle = '#d1d5db';
    ctx.lineWidth = 2;
    ctx.stroke();
    
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 16px Arial';
    ctx.textAlign = 'center';
    ctx.fillText(`${total}`, centerX, centerY - 2);
    ctx.font = '10px Arial';
    ctx.fillText('/ 25', centerX, centerY + 12);
  };

  React.useEffect(() => {
    if (!loading) {
      drawRadarChart();
    }
  }, [ratings, loading]);

  const handleRatingChange = (category, value) => {
    setRatings(prev => ({
      ...prev,
      [category]: parseInt(value)
    }));
  };

  const handleSave = () => {
    if (onSave) {
      onSave({
        studentId: student._id,
        studentName: student.name,
        behaviorRatings: ratings,
        evaluationDate: new Date().toISOString(),
        evaluatedBy: 'Teacher'
      });
    }
    onClose();
  };

  const downloadChart = () => {
    const canvas = canvasRef.current;
    const link = document.createElement('a');
    link.download = `${student.name}_Behavior_Evaluation.png`;
    link.href = canvas.toDataURL();
    link.click();
  };

  const printChart = () => {
    const canvas = canvasRef.current;
    const printWindow = window.open('', '_blank');
    const img = canvas.toDataURL();
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Behavior Evaluation - ${student.name}</title>
          <style>
            body { 
              font-family: Arial, sans-serif; 
              text-align: center; 
              margin: 20px;
            }
            .header {
              margin-bottom: 20px;
            }
            .ratings-table {
              margin: 20px auto;
              border-collapse: collapse;
              width: 80%;
            }
            .ratings-table th, .ratings-table td {
              border: 1px solid #ddd;
              padding: 8px;
              text-align: left;
            }
            .ratings-table th {
              background-color: #f2f2f2;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h2>Student Behavior Evaluation</h2>
            <h3>${student.name} - Class ${student.class}</h3>
            <p>Evaluation Date: ${new Date().toLocaleDateString()}</p>
          </div>
          <img src="${img}" style="max-width: 100%; height: auto;" />
          <table class="ratings-table">
            <tr>
              <th>Behavior Category</th>
              <th>Rating (1-5)</th>
            </tr>
            ${categories.map(cat => `
              <tr>
                <td>${cat.label}</td>
                <td>${ratings[cat.key]}/5</td>
              </tr>
            `).join('')}
          </table>
          <p><strong>Overall Score: ${Object.values(ratings).reduce((a, b) => a + b, 0)}/25</strong></p>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: 'rgba(0,0,0,0.8)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <div style={{
        background: 'white',
        borderRadius: '15px',
        padding: '30px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 40px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px',
          borderBottom: '2px solid #e5e7eb',
          paddingBottom: '15px'
        }}>
          <div>
            <h2 style={{ margin: 0, color: '#1f2937', fontSize: '24px' }}>
              📊 Student Behavior Evaluation
            </h2>
            <p style={{ margin: '5px 0 0 0', color: '#6b7280', fontSize: '16px' }}>
              {student.name} - Class {student.class}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: '#ef4444',
              color: 'white',
              border: 'none',
              padding: '10px 15px',
              borderRadius: '50%',
              cursor: 'pointer',
              fontSize: '18px',
              width: '45px',
              height: '45px'
            }}
          >
            ✕
          </button>
        </div>

        <div style={{ display: window.innerWidth <= 768 ? 'block' : 'grid', gridTemplateColumns: window.innerWidth <= 768 ? '1fr' : '1fr 280px', gap: '20px' }}>
          {/* Chart */}
          <div style={{ textAlign: 'center' }}>
            {loading ? (
              <div style={{
                width: '400px',
                height: '400px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: '2px solid #e5e7eb',
                borderRadius: '10px',
                background: '#fafafa',
                fontSize: '16px',
                color: '#666'
              }}>
                📊 Loading evaluation data...
              </div>
            ) : (
              <canvas
                ref={canvasRef}
                width={window.innerWidth <= 768 ? 300 : 400}
                height={window.innerWidth <= 768 ? 300 : 400}
                style={{
                  border: '2px solid #e5e7eb',
                  borderRadius: '10px',
                  background: '#fafafa',
                  maxWidth: '100%'
                }}
              />
            )}
          </div>

          {/* Controls */}
          <div>
            <h3 style={{ color: '#374151', marginBottom: '20px' }}>Rate Each Category (1-5)</h3>
            
            {categories.map((category) => (
              <div key={category.key} style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  fontWeight: 'bold',
                  color: getColor(ratings[category.key]),
                  fontSize: '14px'
                }}>
                  {category.label}
                </label>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input
                    type="range"
                    min="1"
                    max="5"
                    value={ratings[category.key]}
                    onChange={(e) => handleRatingChange(category.key, e.target.value)}
                    disabled={loading}
                    style={{
                      flex: 1,
                      height: '8px',
                      borderRadius: '4px',
                      background: `linear-gradient(to right, ${category.color} 0%, ${category.color} ${(ratings[category.key] - 1) * 25}%, #e5e7eb ${(ratings[category.key] - 1) * 25}%, #e5e7eb 100%)`,
                      outline: 'none',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      opacity: loading ? 0.6 : 1
                    }}
                  />
                  <span style={{
                    minWidth: '30px',
                    textAlign: 'center',
                    fontWeight: 'bold',
                    fontSize: '16px',
                    color: getColor(ratings[category.key])
                  }}>
                    {ratings[category.key]}
                  </span>
                </div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#9ca3af', marginTop: '2px' }}>
                  <span>Poor</span>
                  <span>Excellent</span>
                </div>
              </div>
            ))}

            {/* Overall Score */}
            <div style={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
              color: 'white',
              padding: '15px',
              borderRadius: '10px',
              textAlign: 'center',
              marginBottom: '20px'
            }}>
              <h4 style={{ margin: '0 0 5px 0' }}>Overall Score</h4>
              <div style={{ fontSize: '24px', fontWeight: 'bold' }}>
                {Object.values(ratings).reduce((a, b) => a + b, 0)}/25
              </div>
              <div style={{ fontSize: '12px', opacity: 0.9 }}>
                ({((Object.values(ratings).reduce((a, b) => a + b, 0) / 25) * 100).toFixed(1)}%)
              </div>
            </div>

            {/* Action Buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                onClick={handleSave}
                disabled={loading}
                style={{
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                💾 {loading ? 'Loading...' : 'Save Evaluation'}
              </button>
              

              
              <button
                onClick={printChart}
                disabled={loading}
                style={{
                  background: loading ? '#9ca3af' : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold'
                }}
              >
                🖨️ Print Evaluation
              </button>
            </div>
          </div>
        </div>

        {/* Legend */}
        <div style={{
          marginTop: '25px',
          padding: '15px',
          background: '#f9fafb',
          borderRadius: '8px',
          border: '1px solid #e5e7eb'
        }}>
          <h4 style={{ margin: '0 0 10px 0', color: '#374151' }}>Rating Scale:</h4>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', fontSize: '12px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#ef4444' }}>1 - Poor</div>
              <div style={{ color: '#6b7280' }}>Needs significant improvement</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#f59e0b' }}>2 - Below Average</div>
              <div style={{ color: '#6b7280' }}>Needs improvement</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#6b7280' }}>3 - Average</div>
              <div style={{ color: '#6b7280' }}>Meets expectations</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#3b82f6' }}>4 - Good</div>
              <div style={{ color: '#6b7280' }}>Above expectations</div>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontWeight: 'bold', color: '#10b981' }}>5 - Excellent</div>
              <div style={{ color: '#6b7280' }}>Exceeds expectations</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StudentBehaviorRadarChart;